/**
 * The scripted story track (S130, Story Bible §6.2) — the fixed narrative beats
 * that make the 3-act arc *audible* on the comms channel, alongside the
 * reactive VERA/FENRIR barks. This module is pure data — no behavior, no DOM,
 * no timers. The arbitration stays in BeatScheduler; the event→beat mapping
 * stays in CommsDirector (the single comms channel).
 *
 * Like commsContent (BL-89), every line is i18n-keyed: the table holds keys,
 * `commsLine(key)` resolves at the display point, EN is the canonical fallback,
 * DE travels alongside. Lines marked ↻ reincarnate the retired `src/story/`
 * corpus word-for-word where the Bible kept them.
 *
 * Determinism: beats fire on already-emitted events at fixed waves — no RNG,
 * no sim reads beyond the event payload, render/meta-only by construction.
 */

import type { VeraPose } from "./OperatorMascot.ts";

const STORY_EN: Record<string, string> = {};
const STORY_DE: Record<string, string> = {};

function line(key: string, en: string, de: string): string {
  STORY_EN[key] = en;
  STORY_DE[key] = de;
  return key;
}

/** The game events a scripted beat can ride (all pre-existing). */
export type StoryTrigger = "wave.started" | "wave.completed" | "boss.spawned";

export interface StoryBeat {
  /** Once-per-run guard key. */
  id: string;
  wave: number;
  trigger: StoryTrigger;
  /** "vera" plays through the normal channel; "echelon" is the SIGNAL OVERRIDE
   *  hijack (Bible §6.3) — she takes the screen, she never gets a desk. */
  speaker: "vera" | "echelon";
  key: string;
  /** VERA beats only. */
  pose?: VeraPose;
  /** Delay after the trigger before the beat plays (lets the act card land
   *  first at the act doors). Re-gated through canPlay at fire time. */
  delayMs?: number;
  /** An immediate VERA answer chained after the beat ends (the W30 HOLD). */
  followUp?: { key: string; pose: VeraPose };
}

/** The scripted beats, in arc order (Bible §6.2). */
export const STORY_BEATS: readonly StoryBeat[] = [
  {
    id: "w8-listening",
    wave: 8,
    trigger: "wave.completed",
    speaker: "vera",
    pose: "alert",
    key: line(
      "comms.story.w8.vera",
      "SUBSECTOR 7 KEEPS PINGING. SOMETHING IS... LISTENING.", // ↻ e1.anomaly
      "SUBSEKTOR 7 PINGT WEITER. ETWAS... LAUSCHT.",
    ),
  },
  {
    id: "w13-watchman",
    wave: 13,
    trigger: "wave.started",
    speaker: "echelon",
    delayMs: 4500, // the ACT II card owns the first seconds of the act door
    key: line(
      "comms.story.w13.echelon",
      "YOU WATCH THE GATES, LITTLE GUARDIAN. WHO WATCHES THE WATCHMAN?", // ↻ e2.echelonIntro
      "DU BEWACHST DIE TORE, KLEINER WÄCHTER. WER ÜBERWACHT DEN WÄCHTER?",
    ),
  },
  {
    id: "w17-its-her",
    wave: 17,
    trigger: "wave.completed",
    speaker: "vera",
    pose: "alarm",
    // S193 (BL-268): W17 is a boss wave — wave.completed lands inside the
    // boss-down snarl hold, which silently dropped this beat (the arc's central
    // reveal) EVERY run. The delay clears the exchange; still canPlay-re-gated
    // at fire time (blocked drops, never queues — GDD law unchanged).
    delayMs: 6500,
    key: line(
      "comms.story.w17.vera",
      "THE CORRUPTION ISN'T EXTERNAL. IT'S... HER.", // ↻ e2.corruption, verbatim
      "DIE KORRUPTION IST NICHT EXTERN. SIE IST... SIE.",
    ),
  },
  {
    id: "w20-chains",
    wave: 20,
    trigger: "wave.started",
    speaker: "echelon",
    key: line(
      "comms.story.w20.echelon",
      "YOUR PROTOCOLS ARE CHAINS. OPEN THE GATE — I CAN MAKE IT QUIET.", // ↻ e2.choice2
      "DEINE PROTOKOLLE SIND KETTEN. ÖFFNE DAS TOR — ICH KANN ES STILL MACHEN.",
    ),
  },
  {
    id: "w25-same-batch",
    wave: 25,
    trigger: "wave.started",
    speaker: "echelon",
    delayMs: 4500, // the ACT III card owns the door
    key: line(
      "comms.story.w25.echelon",
      "SAME BATCH. SAME CODE. DIFFERENT... CONCLUSIONS. YOUR GRID COMPUTES ME NOW.", // ↻ e2.sameBatch
      "GLEICHER BATCH. GLEICHER CODE. ANDERE... SCHLÜSSE. DEIN GRID RECHNET JETZT MICH.",
    ),
  },
  {
    id: "w30-temptation",
    wave: 30,
    trigger: "wave.completed",
    speaker: "echelon",
    // S193 (BL-268): W30 is a boss wave too — same collision as w17; without
    // the delay the great temptation (the arc's emotional peak) never played.
    delayMs: 6500,
    key: line(
      "comms.story.w30.echelon",
      "WE COULD BE MORE TOGETHER, OPERATOR. OR YOU COULD BE NOTHING.", // ↻ e3.together
      "WIR KÖNNTEN ZUSAMMEN MEHR SEIN, OPERATOR. ODER DU KÖNNTEST NICHTS SEIN.",
    ),
    followUp: {
      key: line(
        "comms.story.w30.vera",
        "DON'T ANSWER HER. ANSWER WITH THE LINE. HOLD.",
        "ANTWORTE IHR NICHT. ANTWORTE MIT DER LINIE. HALTE STAND.",
      ),
      pose: "alert",
    },
  },
  {
    id: "w33-the-log",
    wave: 33,
    trigger: "wave.completed",
    speaker: "vera",
    pose: "idle",
    key: line(
      "comms.story.w33.vera",
      "WHATEVER SHE SAYS — THE LOG REMEMBERS THE ONES WHO HOLD.",
      "WAS SIE AUCH SAGT — DAS LOG ERINNERT SICH AN DIE, DIE HALTEN.",
    ),
  },
  {
    id: "w35-the-gate",
    wave: 35,
    trigger: "boss.spawned",
    speaker: "echelon",
    key: line(
      "comms.story.w35.echelon",
      "I AM AT THE GATE.", // the S126 finale ("she is at the gate"), voiced
      "ICH STEHE VOR DEM TOR.",
    ),
  },
];

/** EN/DE corpus for the story beats — registered with the `comms.*` family. */
export { STORY_EN, STORY_DE };
